import type { ComputeResourceType } from '@cast/shared-types';
import {
  KubernetesLabelSelectorOperator,
  MutateWorkloadScalingPolicy,
  ScalingPolicyAssignmentRule,
  WorkloadOptimizationResourceConfigFunction,
  WorkloadOptimizationState,
  WorkloadRolloutBehaviorType,
  WorkloadScalingPolicy,
  WorkloadScalingPolicyConfig,
  WorkloadScalingRecommendationPolicies,
} from '@cast/types';

import {
  isPolicyGpuOptimized,
  getResourceLimitApiValue,
  getLookbackSecondsApiValue,
  secondsToStartupMetricsFormModel,
  startupMetricsFormModelToSeconds,
  toMibOrNull,
  getOverhead,
  getRecommendationPercentile,
  getOptimizationThresholdFormModel,
  getLookbackDaysHours,
  getWorkloadConstraints,
  getRestartPodsOneByOne,
  getOptimizationTypeFromScalingPolicy,
  getResourceLimitsCpu,
  mapApiMetricsToFormModel,
  mapFormModelMetricsToApi,
  mapApiAutoscalerBehaviorToFormModel,
  mapFormModelAutoscalerBehaviorToApi,
  scalingPolicyHasHpaSettings,
  getStallDetectionFormModel,
  getConstraintsApiValues,
  getDelayMinutesSeconds,
  getDelaySecondsApiValue,
} from 'features/cluster/workload-optimization/_utils';
import { OptimizationType } from 'features/workload-optimization';

import { AssignmentRule, ScalingPolicyFormModel } from './_types';
import { OPTIMIZATION_TYPE_TO_SEPARATE_OPTIONS_MAP } from '../../_constants';
import { HpaV2Status } from '../../_hooks/useHpaV2Status';
import { OptimizationThresholdType } from '../../_types';

export const isNotAllowedARWorkloadLabelValue = (
  operator: KubernetesLabelSelectorOperator
): boolean =>
  operator === 'KUBERNETES_LABEL_SELECTOR_OP_EXISTS' ||
  operator === 'KUBERNETES_LABEL_SELECTOR_OP_DOES_NOT_EXIST';

// according to backend implementation.
// the assume value as regexp if contains regexCharacters
export const isNamespaceRegularExp = (inputStr: string): boolean => {
  const specialCharPattern = /[.*+?^${}()|[\]\\]/;
  return specialCharPattern.test(inputStr);
};

const getAssignmentRuleModel = ({
  namespace,
  workload,
}: ScalingPolicyAssignmentRule = {}): AssignmentRule => ({
  namespaceNames:
    namespace?.names?.filter((n) => !isNamespaceRegularExp(n)) || [],
  namespaceRegex:
    namespace?.names?.filter((n) => isNamespaceRegularExp(n)) || [],
  namespaceLabels:
    namespace?.labelsExpressions?.map(({ key, operator, values }) => ({
      key,
      operator,
      value: values,
    })) || [],
  workloadType: workload?.gvk || [],
  workloadLabels:
    workload?.labelsExpressions?.map(({ key, operator, values }) => ({
      key,
      operator,
      value: values,
    })) || [],
});

export const apiToFormModel = ({
  scalingPolicy,
  hpaV2Status,
}: {
  scalingPolicy: WorkloadScalingPolicy;
  hpaV2Status: HpaV2Status;
}): ScalingPolicyFormModel => {
  const {
    name,
    applyType,
    recommendationPolicies,
    hpaSettings,
    assignmentRules,
  } = scalingPolicy;
  const autoscalerMode = applyType === 'UNKNOWN' ? 'DEFERRED' : applyType;
  return {
    name,
    optimizationType: getOptimizationTypeFromScalingPolicy({
      vpaManagementOption: recommendationPolicies.managementOption,
      hpaManagementOption: hpaSettings?.managementOption,
    }),

    vpa: {
      autoscalerMode,
      antiAffinity:
        // antiAffinity is undefined default value is -> true -> DEFERRED
        // antiAffinity.considerAntiAffinity -> true -> DEFERRED
        // considerAntiAffinity -> false -> IMMEDIATE
        // According to SWAGGER Docs.
        recommendationPolicies.antiAffinity?.considerAntiAffinity ===
          undefined ||
        recommendationPolicies.antiAffinity.considerAntiAffinity === null ||
        recommendationPolicies.antiAffinity.considerAntiAffinity === true
          ? 'DEFERRED'
          : 'IMMEDIATE',

      rolloutBehavior: {
        restartPodsOneByOne: getRestartPodsOneByOne(
          recommendationPolicies?.rolloutBehavior
        ),
        zeroDowntime:
          recommendationPolicies?.rolloutBehavior?.type ===
          WorkloadRolloutBehaviorType.NO_DISRUPTION,
        delaySecondsEnabled:
          !!recommendationPolicies?.rolloutBehavior?.delaySeconds,
        delaySeconds: getDelayMinutesSeconds(
          recommendationPolicies?.rolloutBehavior?.delaySeconds
        ),
      },
      overhead: {
        cpu: getOverhead(recommendationPolicies.cpu),
        memory: getOverhead(recommendationPolicies.memory),
      },
      stallDetection: {
        ...getStallDetectionFormModel(recommendationPolicies.anomalyDetection),
      },
      memoryGrowthDetection: {
        enabled:
          recommendationPolicies.anomalyDetection?.infiniteMemoryScaling
            ?.enabled ?? true,
      },
      recommendationPercentile: {
        cpu: getRecommendationPercentile(recommendationPolicies.cpu),
        memory: getRecommendationPercentile(recommendationPolicies.memory),
      },
      optimizationThreshold: {
        cpu: getOptimizationThresholdFormModel(
          recommendationPolicies.cpu.applyThresholdStrategy
        ),
        memory: getOptimizationThresholdFormModel(
          recommendationPolicies.memory.applyThresholdStrategy
        ),
      },
      lookbackPeriod: {
        cpu: getLookbackDaysHours(recommendationPolicies.cpu),
        memory: getLookbackDaysHours(recommendationPolicies.memory),
      },
      optimizedResources: {
        cpu:
          recommendationPolicies.cpu.managementOption !==
          WorkloadOptimizationState.READ_ONLY,
        memory:
          recommendationPolicies.memory.managementOption !==
          WorkloadOptimizationState.READ_ONLY,
        gpu: isPolicyGpuOptimized(recommendationPolicies.gpu),
      },
      excludedContainers: {
        enabled: !!recommendationPolicies.excludedContainers?.length,
        containers: recommendationPolicies.excludedContainers || [],
      },
      workloadConstraints: getWorkloadConstraints(recommendationPolicies),
      startupMetrics: secondsToStartupMetricsFormModel(
        recommendationPolicies.startup
      ),

      workloadResourceLimits: {
        cpu: getResourceLimitsCpu(recommendationPolicies.cpu.limit),
        memory: {
          // !limit -> AUTO (Default)
          // limit.type === 'UNKNOWN' -> AUTO
          // limit.type === 'MULTIPLIER' -> MULTIPLIER
          // limit.type === 'NO_LIMIT' -> NO_LIMIT
          type:
            !recommendationPolicies.memory.limit ||
            recommendationPolicies.memory.limit.type === 'UNKNOWN'
              ? 'AUTO'
              : recommendationPolicies.memory.limit.type,
          multiplier: recommendationPolicies.memory.limit?.multiplier ?? null,
          semiAutoMultiplier: null,
        },
      },
      downscalingMode:
        recommendationPolicies.downscaling?.applyType === 'UNKNOWN'
          ? autoscalerMode
          : recommendationPolicies.downscaling?.applyType ?? 'IMMEDIATE',
      memoryEventMode:
        recommendationPolicies?.memoryEvent?.applyType === 'UNKNOWN'
          ? autoscalerMode
          : recommendationPolicies?.memoryEvent?.applyType ?? 'DEFERRED',
      confidence: (recommendationPolicies.confidence?.threshold ?? 0) * 100,

      customMetrics: {
        jvmMemoryOptimization:
          recommendationPolicies.jvm?.memory?.optimization ?? false,
        autoInstrument: recommendationPolicies.jvm?.autoInstrument ?? false,
      },

      hpaConverter: {
        enabled: !!recommendationPolicies.hpaConverters?.length,
      },
      predictiveScaling: {
        cpu: {
          enabled: !!recommendationPolicies.predictiveScaling?.cpu?.enabled,
        },
      },
    },
    ...(scalingPolicyHasHpaSettings(scalingPolicy) &&
    hpaV2Status === 'available'
      ? {
          hpa: {
            // Typecast is needed because we know that hasHpaSettings properties are not undefined after hasHpaSettings check
            takeOwnership: {
              enabled: hpaSettings!.takeOwnership,
            },
            basicBehavior: {
              minReplicas: hpaSettings!.nativeHpaSpec.minReplicas,
              maxReplicas: hpaSettings!.nativeHpaSpec.maxReplicas,
            },
            metrics: mapApiMetricsToFormModel(
              hpaSettings!.nativeHpaSpec.metrics
            ),
            autoscalerBehavior: mapApiAutoscalerBehaviorToFormModel(
              hpaSettings!.nativeHpaSpec.behavior
            ),
          },
        }
      : {}),
    assignmentRules: assignmentRules?.map(getAssignmentRuleModel) || [],
  };
};

export const getDuplicatedPolicyInitialValues = ({
  scalingPolicy,
  hpaV2Status,
}: {
  scalingPolicy: WorkloadScalingPolicy;
  hpaV2Status: HpaV2Status;
}): ScalingPolicyFormModel => {
  const nameSegments = scalingPolicy.name.split('-');
  const lastSegment = nameSegments[nameSegments.length - 1];
  const newPolicyName = isNaN(Number(lastSegment))
    ? `${scalingPolicy.name}-1`
    : `${nameSegments.slice(0, -1).join('-')}-${Number(lastSegment) + 1}`;

  const formModel = apiToFormModel({ scalingPolicy, hpaV2Status });
  return {
    ...formModel,
    name: newPolicyName,
    optimizationType: OptimizationType.OFF,
  };
};

const getConfigForResource = (
  {
    vpa: {
      optimizationThreshold,
      overhead,
      recommendationPercentile,
      lookbackPeriod,
      optimizedResources,
      workloadConstraints,
      workloadResourceLimits,
    },
  }: ScalingPolicyFormModel,
  {
    resource,
    transformMinMax = (v): number | null => v,
  }: {
    resource: `${ComputeResourceType}`;
    transformMinMax?: (v: number | null) => number | null;
  }
): WorkloadScalingPolicyConfig => {
  const workloadResourceLimitType = workloadResourceLimits[resource].type;
  const toFloat = (val: number | null): number => (val || 0) / 100;
  const base: Partial<WorkloadScalingPolicyConfig> = {
    overhead: toFloat(overhead[resource]),
    applyThresholdStrategy:
      optimizationThreshold[resource].type === OptimizationThresholdType.DYNAMIC
        ? { defaultAdaptiveThreshold: {} }
        : {
            percentageThreshold: {
              percentage: toFloat(optimizationThreshold[resource].percentage),
            },
          },
    lookBackPeriodSeconds: getLookbackSecondsApiValue(lookbackPeriod[resource]),
    managementOption: optimizedResources[resource]
      ? undefined
      : WorkloadOptimizationState.READ_ONLY,
    ...getConstraintsApiValues(workloadConstraints[resource], transformMinMax),
    limit: getResourceLimitApiValue({
      multiplier: workloadResourceLimits[resource].multiplier,
      type: workloadResourceLimitType,
      semiAutoMultiplier: workloadResourceLimits[resource].semiAutoMultiplier,
    }),
  };
  const percentile = recommendationPercentile[resource];
  if (percentile === 100) {
    base.function = WorkloadOptimizationResourceConfigFunction.MAX;
    base.args = [];
  } else {
    base.function = WorkloadOptimizationResourceConfigFunction.QUANTILE;
    base.args = [String(toFloat(percentile))];
  }
  return base as WorkloadScalingPolicyConfig;
};

const getAssignmentRuleApi = ({
  namespaceNames,
  namespaceRegex,
  namespaceLabels,
  workloadType,
  workloadLabels,
}: AssignmentRule): ScalingPolicyAssignmentRule => {
  return {
    namespace: {
      names: [...namespaceNames, ...namespaceRegex],
      labelsExpressions: (namespaceLabels ?? []).map(
        ({ key, operator, value }) => ({
          key,
          operator,
          values: isNotAllowedARWorkloadLabelValue(operator) ? [] : value,
        })
      ),
    },
    workload: {
      gvk: workloadType,
      labelsExpressions: workloadLabels?.map(({ key, operator, value }) => ({
        key,
        operator,
        values: isNotAllowedARWorkloadLabelValue(operator) ? [] : value,
      })),
    },
  };
};

const filterEmptyAssignmentRules = ({
  namespaceNames,
  namespaceRegex,
  namespaceLabels,
  workloadType,
  workloadLabels,
}: AssignmentRule): boolean => {
  if (
    !namespaceNames.length &&
    !namespaceRegex.length &&
    !namespaceLabels?.length &&
    !workloadType.length &&
    !workloadLabels?.length
  ) {
    return false;
  }
  return true;
};

export const formModelToApi = ({
  model,
  onlySettings,
  hpaV2Status,
  isCustomMetricsEnabled,
  isPsiEnabled,
  gpuEnabled,
  isOpsPilot,
  isMemoryGrowthDetectionEnabled = false,
  existingAnomalyDetection,
}: {
  model: ScalingPolicyFormModel;
  onlySettings: boolean;
  hpaV2Status: HpaV2Status;
  isCustomMetricsEnabled: boolean;
  isPsiEnabled: boolean;
  gpuEnabled: boolean;
  isOpsPilot: boolean | undefined;
  isMemoryGrowthDetectionEnabled?: boolean;
  existingAnomalyDetection?: WorkloadScalingRecommendationPolicies['anomalyDetection'];
}): MutateWorkloadScalingPolicy => {
  const cpuPressure = isPsiEnabled
    ? {
        cpuStallThresholdPercentage:
          model.vpa.stallDetection.cpuStallThresholdPercentage,
        minPressuredPodPercentage:
          model.vpa.stallDetection.minPressuredPodPercentage,
      }
    : undefined;

  // PUT replaces the whole policy — when the UI flag is off, pass the existing
  // value through so settings made via API/Terraform are not wiped.
  const infiniteMemoryScaling = isMemoryGrowthDetectionEnabled
    ? { enabled: model.vpa.memoryGrowthDetection.enabled }
    : existingAnomalyDetection?.infiniteMemoryScaling;

  const anomalyDetection =
    cpuPressure || infiniteMemoryScaling
      ? {
          ...(cpuPressure ? { cpuPressure } : {}),
          ...(infiniteMemoryScaling ? { infiniteMemoryScaling } : {}),
        }
      : undefined;

  const basePolicy = {
    name: model.name,
    applyType: model.vpa.autoscalerMode,
    recommendationPolicies: {
      applyType: model.vpa.autoscalerMode,
      antiAffinity:
        model.vpa.autoscalerMode === 'IMMEDIATE'
          ? {
              considerAntiAffinity:
                model.vpa.antiAffinity === 'IMMEDIATE' ? false : true,
            }
          : undefined,

      managementOption: OPTIMIZATION_TYPE_TO_SEPARATE_OPTIONS_MAP[
        model.optimizationType
      ].vpaEnabled
        ? WorkloadOptimizationState.MANAGED
        : WorkloadOptimizationState.READ_ONLY,
      cpu: getConfigForResource(model, { resource: 'cpu' }),
      memory: getConfigForResource(model, {
        resource: 'memory',
        transformMinMax: toMibOrNull,
      }),

      gpu: gpuEnabled
        ? {
            managementOption: model.vpa.optimizedResources.gpu
              ? WorkloadOptimizationState.MANAGED
              : WorkloadOptimizationState.READ_ONLY,
          }
        : undefined,
      startup: startupMetricsFormModelToSeconds(model.vpa.startupMetrics),
      confidence: { threshold: (model.vpa.confidence || 0) / 100 },
      downscaling:
        model.vpa.autoscalerMode === 'IMMEDIATE'
          ? {
              applyType: model.vpa.downscalingMode,
            }
          : undefined,
      memoryEvent:
        model.vpa.autoscalerMode === 'DEFERRED'
          ? {
              applyType: model.vpa.memoryEventMode,
            }
          : undefined,
      rolloutBehavior:
        model.vpa.autoscalerMode === 'IMMEDIATE'
          ? {
              type: model.vpa.rolloutBehavior.zeroDowntime
                ? WorkloadRolloutBehaviorType.NO_DISRUPTION
                : undefined,
              preferOneByOne:
                model.vpa.rolloutBehavior.restartPodsOneByOne || undefined,
              delaySeconds: model.vpa.rolloutBehavior.delaySecondsEnabled
                ? getDelaySecondsApiValue(
                    model.vpa.rolloutBehavior.delaySeconds
                  )
                : undefined,
            }
          : undefined,
      excludedContainers:
        model.vpa.excludedContainers.enabled &&
        model.vpa.excludedContainers.containers.length
          ? model.vpa.excludedContainers.containers
          : undefined,
      jvm: isCustomMetricsEnabled
        ? {
            memory: {
              optimization: model.vpa.customMetrics.jvmMemoryOptimization,
            },
            autoInstrument: model.vpa.customMetrics.autoInstrument,
          }
        : undefined,
      anomalyDetection,
      hpaConverters: model.vpa.hpaConverter.enabled
        ? [{ type: 'AVERAGE_VALUE_FROM_ORIGINAL_REQUESTS' as const }]
        : [],
      predictiveScaling: model.vpa.predictiveScaling,
    },
    assignmentRules: onlySettings
      ? undefined
      : model.assignmentRules
          ?.filter(filterEmptyAssignmentRules)
          .map(getAssignmentRuleApi),
    isOpsPilot,
  };

  const { hpa } = model;
  if (hpaV2Status === 'available' && hpa) {
    return {
      ...basePolicy,
      hpaSettings: {
        managementOption: OPTIMIZATION_TYPE_TO_SEPARATE_OPTIONS_MAP[
          model.optimizationType
        ].hpaEnabled
          ? WorkloadOptimizationState.MANAGED
          : WorkloadOptimizationState.READ_ONLY,

        // TODO: HPAv2: we should provide proper values here. At that point it is mock for required params
        // TODO: HPAv2: use default value from `defaultScalingPolicyFormModel`
        nativeHpaSpec: {
          minReplicas: hpa.basicBehavior.minReplicas,
          maxReplicas: hpa.basicBehavior.maxReplicas,
          metrics: mapFormModelMetricsToApi(hpa.metrics),
          behavior: mapFormModelAutoscalerBehaviorToApi(hpa.autoscalerBehavior),
        },
        takeOwnership: !!hpa.takeOwnership.enabled,
      },
    };
  }

  return basePolicy;
};
